// Extra per il compose della webmail: rubrica destinatari + allegati dal gestionale
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FileText, Search, Plus, Check, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RecipientInput = ({ value, onChange, placeholder, testId }) => {
  const [sugg, setSugg] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef();

  const handle = (v) => {
    onChange(v);
    const term = v.split(",").pop().trim();
    clearTimeout(timer.current);
    if (term.length < 2) { setSugg([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/webmail/contacts`, { params: { q: term } });
        setSugg(r.data);
        setOpen(r.data.length > 0);
      } catch { setSugg([]); setOpen(false); }
    }, 250);
  };

  const pick = (c) => {
    const parts = value.split(",");
    parts[parts.length - 1] = (parts.length > 1 ? " " : "") + c.email;
    onChange(parts.join(","));
    setSugg([]);
    setOpen(false);
  };

  return (
    <div className="relative flex-1">
      <Input value={value} onChange={(e) => handle(e.target.value)} placeholder={placeholder}
        onBlur={() => setTimeout(() => setOpen(false), 150)} data-testid={testId} autoComplete="off" />
      {open && (
        <div className="absolute z-[60] top-full left-0 right-0 bg-white border border-stone-200 rounded-md shadow-lg mt-1 max-h-56 overflow-y-auto" data-testid="contacts-dropdown">
          {sugg.map((c, i) => (
            <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              className="w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center gap-2"
              data-testid={`contact-sugg-${i}`}>
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${c.kind === "cliente" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>{c.kind}</span>
              <span className="text-sm font-medium text-stone-800 truncate">{c.name}</span>
              <span className="text-xs text-stone-400 truncate ml-auto">{c.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TABS = [
  { id: "orders", label: "Ordini" },
  { id: "ddt", label: "DDT" },
  { id: "offers", label: "Listini" },
  { id: "dedicated", label: "Listini dedicati" },
];

export const DocPicker = ({ open, onClose, onPick, picked }) => {
  const [tab, setTab] = useState("orders");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setData(null);
    (async () => {
      const safe = (p) => p.then((r) => r.data).catch(() => []);
      const [orders, ddtA, ddtB, offers, dedicated] = await Promise.all([
        safe(axios.get(`${API}/orders`)),
        safe(axios.get(`${API}/sales-documents`, { params: { with_order: true } })),
        safe(axios.get(`${API}/sales-documents`)),
        safe(axios.get(`${API}/offers`)),
        safe(axios.get(`${API}/dedicated-offers`)),
      ]);
      const ddt = [...(Array.isArray(ddtA) ? ddtA : []), ...(Array.isArray(ddtB) ? ddtB : [])].filter((d) => d.ddt_attachment?.path);
      setData({
        orders: (Array.isArray(orders) ? orders : []).slice(0, 200).map((o) => ({
          type: "order", id: o.id, label: `Conferma ordine ${o.order_number || ""}`,
          sub: `${o.customer?.customer_name || o.customer?.business_name || ""} · ${(o.created_at || "").slice(0, 10).split("-").reverse().join("/")}`,
        })),
        ddt: ddt.slice(0, 200).map((d) => ({
          type: "ddt", id: d.id, label: `DDT ${d.doc_number || ""}`,
          sub: [
            d.customer_snapshot?.business_name || d.customer_name || "",
            d.order_number ? `Ordine ${d.order_number}` : "",
            d.document_reference && d.document_reference !== d.doc_number ? `Rif. ${d.document_reference}` : "",
            (d.doc_date || "").slice(0, 10).split("-").reverse().join("/"),
          ].filter(Boolean).join(" · "),
        })),
        offers: (Array.isArray(offers) ? offers : []).map((o) => ({
          type: "offer", id: o.id, label: `Listino: ${o.title || ""}`, sub: (o.expires_at || "").slice(0, 10),
        })),
        dedicated: (Array.isArray(dedicated) ? dedicated : []).map((o) => ({
          type: "dedicated_offer", id: o.id, label: `Listino dedicato: ${o.title || o.name || ""}`, sub: o.customer_name || "",
        })),
      });
    })();
  }, [open]);

  const rows = (data?.[tab] || []).filter((r) => !q.trim() || `${r.label} ${r.sub}`.toLowerCase().includes(q.trim().toLowerCase()));
  const isPicked = (r) => picked?.some((p) => p.type === r.type && p.id === r.id);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col" data-testid="doc-picker-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#8A1C2A]" />Allega dal gestionale</DialogTitle></DialogHeader>
        <div className="flex gap-1 border-b border-stone-200 shrink-0 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === t.id ? "border-[#8A1C2A] text-[#8A1C2A]" : "border-transparent text-stone-500"}`}
              data-testid={`docpicker-tab-${t.id}`}>
              {t.label}{data ? ` (${(data[t.id] || []).length})` : ""}
            </button>
          ))}
        </div>
        <div className="relative shrink-0">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="pl-9" data-testid="docpicker-search" />
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-stone-100">
          {!data && <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin text-stone-300 mx-auto" /></div>}
          {data && rows.length === 0 && <p className="p-6 text-center text-sm text-stone-400">Nessun documento</p>}
          {rows.map((r) => (
            <div key={`${r.type}-${r.id}`} className="flex items-center gap-3 px-2 py-2">
              <FileText className="w-4 h-4 text-stone-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-800 truncate">{r.label}</p>
                <p className="text-xs text-stone-400 truncate">{r.sub}</p>
              </div>
              {isPicked(r) ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold px-2"><Check className="w-3.5 h-3.5" /> Allegato</span>
              ) : (
                <Button size="sm" variant="outline" onClick={() => onPick(r)} data-testid={`docpick-${r.type}-${r.id}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Allega
                </Button>
              )}
            </div>
          ))}
        </div>
        <div className="shrink-0 pt-2 border-t border-stone-100 text-right">
          <Button onClick={onClose} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="docpicker-done">Fatto</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
